import React, { useId, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, Chip, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import jwtAxios from '@tenant/services/auth/jwt-auth';
import PropTypes from 'prop-types';
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'personas';
const KEY = 'Name';
const LABEL = 'Name';

const PersonasSelect = (props) => {
  const {
    error,
    helperText,
    value = [],
    required,
    handleInputChange,
    excludes = [],
    textFieldProps,
    isMultiple = true,
    label,
    queryKey,
    ...rest
  } = props;
  const [isEnabled, setIsEnabled] = useState(false);

  const { data, isLoading, isRefetching } = useQuery({
    enabled: isEnabled,
    queryKey: ['select-personas', queryKey],
    queryFn: () => jwtAxios.get('/api/apps/app-personas'),
  });

  const id = useId();

  const onOpen = () => {
    setIsEnabled(true);
  };

  return (
    <Autocomplete
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={value}
      id={`${FIELD_NAME}-select-${id}`}
      name={FIELD_NAME}
      options={
        data?.data?.filter((e) => !excludes?.some((f) => f[KEY] === e[KEY])) ??
        []
      }
      onOpen={onOpen}
      getOptionLabel={(option) => option[LABEL]}
      isOptionEqualToValue={(option, value) => {
        return option?.[KEY] === value?.[KEY];
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li {...optionProps} key={option?.[KEY]}>
            {isMultiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {option[LABEL]}
          </li>
        );
      }}
      loading={isLoading || isRefetching}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={isMultiple ? 'Select Personas' : 'Select Persona'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => (
          <Chip
            label={option[LABEL]}
            {...getTagProps({ index })}
            key={option[KEY]}
          />
        ));
      }}
      {...rest}
    />
  );
};

export default React.memo(PersonasSelect);
PersonasSelect.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  required: PropTypes.bool,
  handleInputChange: PropTypes.func,
  excludes: PropTypes.array,
  textFieldProps: PropTypes.object,
  isMultiple: PropTypes.bool,
  label: PropTypes.string,
  queryKey: PropTypes.string,
};
